import { COMPANY, addressLine, LEGAL_VERSION, type LegalDocument } from './legal.js';

/**
 * Terms and privacy in every language the portal offers.
 *
 * Written rather than machine translated. A customer cannot give informed
 * consent to a charge in a language they do not read, and a liability clause
 * that says something slightly different in each language is worse than no
 * translation at all.
 *
 * The substance is identical across languages; only the wording differs. When
 * one changes, all of them change, and LEGAL_VERSION is bumped.
 */

const C = COMPANY.legalName;
const E = COMPANY.email;
const P = COMPANY.phone;

type Lang = 'en' | 'bg' | 'de' | 'fr' | 'es' | 'it' | 'ru' | 'uk';

function doc(title: string, intro: string, sections: Array<[string, string[]]>): LegalDocument {
  return { title, updated: LEGAL_VERSION, intro, sections: sections.map(([heading, body]) => ({ heading, body })) };
}

const identity = (managing: string, contact: string) => [
  `${C} (WyoCSPay)`,
  addressLine,
  `${managing} ${COMPANY.managingMember}`,
  `${contact} ${E} · ${P}`,
];

// ---------------------------------------------------------------------------

const terms: Record<Lang, LegalDocument> = {
  en: doc('Terms of Service',
    `These terms govern your use of the WyoCSPay payment page operated by ${C}, a limited liability company registered in Wyoming, United States.`,
    [
      ['What this page is', [
        'WyoCSPay is a payment page. We are not the seller. The goods or services you are paying for are supplied by the merchant named on this page, and your contract for them is with that merchant, not with us.',
        'We pass your payment to the merchant through Stripe, our payment processor. We do not take possession of your money: the charge settles directly to the merchant’s own account with Stripe.',
      ]],
      ['Who to contact about what', [
        'Questions about the goods or services, delivery, cancellation or a refund: contact the merchant. Those decisions are theirs and we cannot make them on their behalf.',
        `Questions about the payment page itself, or if a charge appears twice or for the wrong amount: contact us at ${E} or ${P}. Quote the payment reference shown at the top of the page.`,
      ]],
      ['Card details', [
        'Your card details are entered directly into a form hosted by Stripe and are transmitted to Stripe. They do not pass through, and are not stored on, our servers. We receive only the card brand and the last four digits, so that a payment can be identified later.',
      ]],
      ['When a payment is complete', [
        'A payment is complete only when the payment network confirms it to our servers. The page returning to a success screen is not, on its own, confirmation. If the page says we are still confirming, the payment has not yet been finalised.',
        'If a payment is declined, no money leaves your account. Your bank may show a temporary authorisation hold, which it releases on its own schedule.',
      ]],
      ['Refunds and disputes', [
        'Refunds are issued by the merchant. We process a refund when the merchant instructs it, and the money returns by the route it came.',
        'You retain whatever chargeback rights your card issuer gives you. Nothing here limits them.',
      ]],
      ['Availability and liability', [
        'We provide this page as it is and do not promise uninterrupted availability.',
        'We are not liable for the merchant’s conduct, for goods or services they supply, or for any dispute between you and them. Nothing here excludes liability that cannot lawfully be excluded.',
      ]],
      ['Governing law', [
        'These terms are governed by the laws of the State of Wyoming, United States. Consumer protection rights available to you in your country of residence are unaffected.',
      ]],
      ['Who we are', identity('Managing member:', 'Email:')],
    ]),

  bg: doc('Общи условия',
    `Настоящите условия уреждат ползването на платежната страница WyoCSPay, управлявана от ${C}, дружество с ограничена отговорност, регистрирано в щата Уайоминг, САЩ.`,
    [
      ['Какво представлява тази страница', [
        'WyoCSPay е платежна страница. Ние не сме продавачът. Стоките или услугите, които плащате, се предоставят от посочения тук търговец, и договорът за тях е между Вас и него, не с нас.',
        'Предаваме плащането Ви на търговеца чрез Stripe, нашия платежен процесор. Ние не държим парите Ви: сумата постъпва директно по сметката на търговеца в Stripe.',
      ]],
      ['Към кого да се обърнете', [
        'За въпроси относно стоката или услугата, доставката, отказа или възстановяване на сума се обърнете към търговеца. Тези решения са негови и не можем да ги вземем вместо него.',
        `За въпроси относно самата платежна страница, или ако сума е удържана двойно или в грешен размер, пишете на ${E} или се обадете на ${P}. Посочете референцията на плащането от горната част на страницата.`,
      ]],
      ['Данни на картата', [
        'Данните на картата Ви се въвеждат директно във форма, поддържана от Stripe, и се предават на него. Те не преминават през и не се съхраняват на нашите сървъри. Получаваме само марката на картата и последните четири цифри, за да може плащането да бъде разпознато по-късно.',
      ]],
      ['Кога плащането е завършено', [
        'Плащането е завършено едва когато платежната мрежа го потвърди на нашите сървъри. Връщането на страницата към екран за успех само по себе си не е потвърждение. Ако страницата казва, че потвърждаваме, плащането още не е финализирано.',
        'При отказано плащане не се удържат средства. Банката Ви може да покаже временно блокирана сума, която освобождава по свой график.',
      ]],
      ['Възстановявания и спорове', [
        'Възстановяванията се извършват от търговеца. Обработваме ги по негово нареждане и сумата се връща по пътя, по който е дошла.',
        'Запазвате всички права за оспорване, които Ви дава издателят на картата. Нищо тук не ги ограничава.',
      ]],
      ['Достъпност и отговорност', [
        'Предоставяме страницата такава, каквато е, без обещание за непрекъсваема достъпност.',
        'Не носим отговорност за поведението на търговеца, за предоставяните от него стоки и услуги, нито за спор между Вас и него. Нищо тук не изключва отговорност, която по закон не може да бъде изключена.',
      ]],
      ['Приложимо право', [
        'Настоящите условия се уреждат от правото на щата Уайоминг, САЩ. Потребителските права по правото на държавата Ви на пребиваване остават незасегнати.',
      ]],
      ['Кои сме ние', identity('Управляващ съдружник:', 'Имейл:')],
    ]),

  de: doc('Nutzungsbedingungen',
    `Diese Bedingungen regeln Ihre Nutzung der WyoCSPay-Zahlungsseite, betrieben von ${C}, einer Gesellschaft mit beschränkter Haftung mit Sitz in Wyoming, USA.`,
    [
      ['Was diese Seite ist', [
        'WyoCSPay ist eine Zahlungsseite. Wir sind nicht der Verkäufer. Die Waren oder Dienstleistungen liefert der auf dieser Seite genannte Händler; Ihr Vertrag besteht mit ihm, nicht mit uns.',
        'Wir leiten Ihre Zahlung über Stripe, unseren Zahlungsdienstleister, an den Händler weiter. Wir nehmen Ihr Geld nicht in Besitz: die Zahlung geht direkt auf das Stripe-Konto des Händlers.',
      ]],
      ['Wen Sie wofür kontaktieren', [
        'Fragen zu Waren, Lieferung, Stornierung oder Erstattung richten Sie bitte an den Händler. Diese Entscheidungen trifft er, nicht wir.',
        `Bei Fragen zur Zahlungsseite selbst, oder wenn eine Belastung doppelt oder in falscher Höhe erscheint, wenden Sie sich an ${E} oder ${P}. Nennen Sie die oben angezeigte Zahlungsreferenz.`,
      ]],
      ['Kartendaten', [
        'Ihre Kartendaten werden direkt in ein von Stripe gehostetes Formular eingegeben und an Stripe übermittelt. Sie passieren unsere Server nicht und werden dort nicht gespeichert. Wir erhalten nur die Kartenmarke und die letzten vier Ziffern, damit eine Zahlung später zugeordnet werden kann.',
      ]],
      ['Wann eine Zahlung abgeschlossen ist', [
        'Eine Zahlung ist erst abgeschlossen, wenn das Zahlungsnetzwerk sie unseren Servern bestätigt. Eine Erfolgsseite allein ist keine Bestätigung. Steht dort, dass wir noch bestätigen, ist die Zahlung noch nicht abgeschlossen.',
        'Bei einer abgelehnten Zahlung wird kein Geld abgebucht. Ihre Bank kann eine vorübergehende Reservierung anzeigen, die sie nach eigenem Zeitplan auflöst.',
      ]],
      ['Erstattungen und Streitfälle', [
        'Erstattungen veranlasst der Händler. Wir führen sie auf seine Anweisung aus; das Geld geht denselben Weg zurück.',
        'Ihre Chargeback-Rechte gegenüber Ihrer Bank bleiben unberührt.',
      ]],
      ['Verfügbarkeit und Haftung', [
        'Wir stellen diese Seite bereit, wie sie ist, ohne ununterbrochene Verfügbarkeit zuzusichern.',
        'Für das Verhalten des Händlers, seine Waren und Dienstleistungen sowie Streitigkeiten zwischen Ihnen und ihm haften wir nicht. Gesetzlich zwingende Haftung bleibt unberührt.',
      ]],
      ['Anwendbares Recht', [
        'Es gilt das Recht des Staates Wyoming, USA. Verbraucherschutzrechte in Ihrem Wohnsitzland bleiben unberührt.',
      ]],
      ['Wer wir sind', identity('Geschäftsführendes Mitglied:', 'E-Mail:')],
    ]),

  fr: doc('Conditions générales',
    `Les présentes conditions régissent votre utilisation de la page de paiement WyoCSPay exploitée par ${C}, société à responsabilité limitée immatriculée dans le Wyoming, États-Unis.`,
    [
      ['Ce qu’est cette page', [
        'WyoCSPay est une page de paiement. Nous ne sommes pas le vendeur. Les biens ou services sont fournis par le marchand indiqué sur cette page, et votre contrat est conclu avec lui, non avec nous.',
        'Nous transmettons votre paiement au marchand via Stripe, notre prestataire. Nous ne détenons pas votre argent : le paiement est versé directement sur le compte Stripe du marchand.',
      ]],
      ['Qui contacter et pourquoi', [
        'Pour les questions sur les biens, la livraison, l’annulation ou un remboursement, contactez le marchand. Ces décisions lui appartiennent.',
        `Pour les questions sur la page de paiement, ou si un débit apparaît deux fois ou pour un montant erroné, écrivez à ${E} ou appelez le ${P}. Indiquez la référence affichée en haut de la page.`,
      ]],
      ['Données de carte', [
        'Vos données de carte sont saisies directement dans un formulaire hébergé par Stripe et lui sont transmises. Elles ne transitent pas par nos serveurs et n’y sont pas stockées. Nous recevons uniquement la marque et les quatre derniers chiffres, afin d’identifier un paiement ultérieurement.',
      ]],
      ['Quand un paiement est finalisé', [
        'Un paiement n’est finalisé que lorsque le réseau le confirme à nos serveurs. Le retour sur un écran de succès ne constitue pas à lui seul une confirmation.',
        'En cas de refus, aucun montant n’est débité. Votre banque peut afficher une empreinte temporaire, qu’elle libère selon son propre calendrier.',
      ]],
      ['Remboursements et litiges', [
        'Les remboursements sont décidés par le marchand. Nous les exécutons sur son instruction et les fonds reviennent par le même chemin.',
        'Vos droits de rétrofacturation auprès de votre banque restent intacts.',
      ]],
      ['Disponibilité et responsabilité', [
        'Nous fournissons cette page en l’état, sans garantie de disponibilité continue.',
        'Nous ne répondons pas de la conduite du marchand, de ses biens et services, ni des litiges entre vous et lui. Les responsabilités légalement incompressibles demeurent.',
      ]],
      ['Droit applicable', [
        'Les présentes conditions sont régies par le droit de l’État du Wyoming, États-Unis. Vos droits de consommateur dans votre pays de résidence demeurent.',
      ]],
      ['Qui nous sommes', identity('Membre gérant :', 'E-mail :')],
    ]),

  es: doc('Términos del servicio',
    `Estos términos rigen su uso de la página de pago WyoCSPay operada por ${C}, sociedad de responsabilidad limitada registrada en Wyoming, Estados Unidos.`,
    [
      ['Qué es esta página', [
        'WyoCSPay es una página de pago. No somos el vendedor. Los bienes o servicios los suministra el comercio indicado en esta página, y su contrato es con él, no con nosotros.',
        'Transmitimos su pago al comercio a través de Stripe, nuestro procesador. No tomamos posesión de su dinero: el cargo se liquida directamente en la cuenta Stripe del comercio.',
      ]],
      ['A quién contactar', [
        'Para preguntas sobre bienes, entrega, cancelación o reembolso, contacte con el comercio. Esas decisiones son suyas.',
        `Para dudas sobre la página de pago, o si un cargo aparece dos veces o por un importe incorrecto, escriba a ${E} o llame al ${P}. Indique la referencia mostrada arriba.`,
      ]],
      ['Datos de la tarjeta', [
        'Los datos de su tarjeta se introducen directamente en un formulario alojado por Stripe y se transmiten a Stripe. No pasan por nuestros servidores ni se almacenan en ellos. Solo recibimos la marca y los últimos cuatro dígitos, para poder identificar un pago después.',
      ]],
      ['Cuándo se completa un pago', [
        'Un pago solo se completa cuando la red de pagos lo confirma a nuestros servidores. Que la página muestre una pantalla de éxito no es, por sí solo, una confirmación.',
        'Si se rechaza el pago, no se retira dinero. Su banco puede mostrar una retención temporal que libera según su propio calendario.',
      ]],
      ['Reembolsos y disputas', [
        'Los reembolsos los emite el comercio. Los procesamos cuando lo indica y el dinero vuelve por la misma vía.',
        'Sus derechos de retrocesión ante su banco permanecen intactos.',
      ]],
      ['Disponibilidad y responsabilidad', [
        'Ofrecemos esta página tal cual, sin prometer disponibilidad ininterrumpida.',
        'No respondemos de la conducta del comercio, de sus bienes o servicios, ni de disputas entre usted y él. Las responsabilidades legalmente inexcluibles permanecen.',
      ]],
      ['Ley aplicable', [
        'Estos términos se rigen por las leyes del Estado de Wyoming, Estados Unidos. Sus derechos como consumidor en su país de residencia no se ven afectados.',
      ]],
      ['Quiénes somos', identity('Miembro gestor:', 'Correo:')],
    ]),

  it: doc('Termini di servizio',
    `Questi termini disciplinano l’uso della pagina di pagamento WyoCSPay gestita da ${C}, società a responsabilità limitata registrata nel Wyoming, Stati Uniti.`,
    [
      ['Che cos’è questa pagina', [
        'WyoCSPay è una pagina di pagamento. Non siamo il venditore. I beni o servizi sono forniti dall’esercente indicato in questa pagina e il contratto è con lui, non con noi.',
        'Trasmettiamo il pagamento all’esercente tramite Stripe, il nostro processore. Non prendiamo possesso del denaro: l’addebito viene liquidato direttamente sul conto Stripe dell’esercente.',
      ]],
      ['Chi contattare', [
        'Per domande su beni, consegna, annullamento o rimborso, contatta l’esercente. Quelle decisioni spettano a lui.',
        `Per domande sulla pagina di pagamento, o se un addebito appare due volte o per un importo errato, scrivi a ${E} o chiama ${P}. Indica il riferimento mostrato in alto.`,
      ]],
      ['Dati della carta', [
        'I dati della carta vengono inseriti direttamente in un modulo ospitato da Stripe e trasmessi a Stripe. Non transitano né sono conservati sui nostri server. Riceviamo solo il circuito e le ultime quattro cifre, per poter identificare un pagamento in seguito.',
      ]],
      ['Quando un pagamento è completo', [
        'Un pagamento è completo solo quando il circuito lo conferma ai nostri server. Il ritorno a una schermata di successo non è di per sé una conferma.',
        'Se il pagamento viene rifiutato, nessun importo viene prelevato. La banca può mostrare un blocco temporaneo che rilascia secondo i propri tempi.',
      ]],
      ['Rimborsi e contestazioni', [
        'I rimborsi sono disposti dall’esercente. Li elaboriamo su sua istruzione e il denaro torna per la stessa via.',
        'I tuoi diritti di storno presso la banca restano intatti.',
      ]],
      ['Disponibilità e responsabilità', [
        'Forniamo questa pagina così com’è, senza garantire disponibilità continua.',
        'Non rispondiamo della condotta dell’esercente, dei suoi beni o servizi, né di controversie tra voi. Restano le responsabilità non escludibili per legge.',
      ]],
      ['Legge applicabile', [
        'Questi termini sono regolati dalla legge dello Stato del Wyoming, Stati Uniti. I diritti dei consumatori nel tuo paese di residenza restano impregiudicati.',
      ]],
      ['Chi siamo', identity('Membro gestore:', 'Email:')],
    ]),

  ru: doc('Условия использования',
    `Настоящие условия регулируют использование вами платёжной страницы WyoCSPay, управляемой ${C}, компанией с ограниченной ответственностью, зарегистрированной в штате Вайоминг, США.`,
    [
      ['Что представляет собой эта страница', [
        'WyoCSPay это платёжная страница. Мы не являемся продавцом. Товары или услуги предоставляет указанный здесь продавец, и договор заключён с ним, а не с нами.',
        'Мы передаём ваш платёж продавцу через Stripe, наш платёжный процессор. Мы не владеем вашими деньгами: сумма поступает напрямую на счёт продавца в Stripe.',
      ]],
      ['К кому обращаться', [
        'По вопросам о товарах, доставке, отмене или возврате обращайтесь к продавцу. Эти решения принимает он.',
        `По вопросам о самой платёжной странице, либо если списание прошло дважды или на неверную сумму, пишите на ${E} или звоните ${P}. Укажите ссылку платежа, показанную вверху страницы.`,
      ]],
      ['Данные карты', [
        'Данные карты вводятся непосредственно в форму, размещённую Stripe, и передаются ему. Они не проходят через наши серверы и не хранятся на них. Мы получаем только платёжную систему и последние четыре цифры, чтобы платёж можно было опознать позже.',
      ]],
      ['Когда платёж завершён', [
        'Платёж завершён только когда платёжная сеть подтвердит его нашим серверам. Возврат страницы на экран успеха сам по себе подтверждением не является.',
        'При отклонении платежа средства не списываются. Банк может показать временную блокировку, которую снимает по своему графику.',
      ]],
      ['Возвраты и споры', [
        'Возвраты инициирует продавец. Мы выполняем их по его указанию, деньги возвращаются тем же путём.',
        'Ваши права на чарджбэк перед банком сохраняются.',
      ]],
      ['Доступность и ответственность', [
        'Мы предоставляем эту страницу как есть и не обещаем непрерывной доступности.',
        'Мы не отвечаем за поведение продавца, его товары и услуги, а также за споры между вами. Ответственность, которую нельзя исключить по закону, сохраняется.',
      ]],
      ['Применимое право', [
        'Настоящие условия регулируются правом штата Вайоминг, США. Права потребителя по праву страны вашего проживания сохраняются.',
      ]],
      ['Кто мы', identity('Управляющий участник:', 'Эл. почта:')],
    ]),

  uk: doc('Умови користування',
    `Ці умови регулюють використання вами платіжної сторінки WyoCSPay, якою керує ${C}, товариство з обмеженою відповідальністю, зареєстроване у штаті Вайомінг, США.`,
    [
      ['Що це за сторінка', [
        'WyoCSPay це платіжна сторінка. Ми не є продавцем. Товари чи послуги надає зазначений тут продавець, і договір укладено з ним, а не з нами.',
        'Ми передаємо ваш платіж продавцю через Stripe, наш платіжний процесор. Ми не володіємо вашими коштами: сума надходить безпосередньо на рахунок продавця у Stripe.',
      ]],
      ['До кого звертатися', [
        'З питань про товари, доставку, скасування чи повернення звертайтеся до продавця. Ці рішення ухвалює він.',
        `З питань про саму платіжну сторінку, або якщо списання пройшло двічі чи на невірну суму, пишіть на ${E} або телефонуйте ${P}. Вкажіть референс платежу, показаний угорі сторінки.`,
      ]],
      ['Дані картки', [
        'Дані картки вводяться безпосередньо у форму, розміщену Stripe, і передаються йому. Вони не проходять через наші сервери й не зберігаються на них. Ми отримуємо лише платіжну систему та останні чотири цифри, щоб платіж можна було впізнати пізніше.',
      ]],
      ['Коли платіж завершено', [
        'Платіж завершено лише коли платіжна мережа підтвердить його нашим серверам. Повернення сторінки на екран успіху саме по собі не є підтвердженням.',
        'У разі відхилення платежу кошти не списуються. Банк може показати тимчасове блокування, яке знімає за власним графіком.',
      ]],
      ['Повернення та спори', [
        'Повернення ініціює продавець. Ми виконуємо їх за його вказівкою, кошти повертаються тим самим шляхом.',
        'Ваші права на чарджбек перед банком зберігаються.',
      ]],
      ['Доступність та відповідальність', [
        'Ми надаємо цю сторінку як є і не обіцяємо безперервної доступності.',
        'Ми не відповідаємо за поведінку продавця, його товари та послуги, а також за спори між вами. Відповідальність, яку не можна виключити за законом, зберігається.',
      ]],
      ['Застосовне право', [
        'Ці умови регулюються правом штату Вайомінг, США. Права споживача за правом країни вашого проживання зберігаються.',
      ]],
      ['Хто ми', identity('Керуючий учасник:', 'Ел. пошта:')],
    ]),
};

// ---------------------------------------------------------------------------

const privacy: Record<Lang, LegalDocument> = {
  en: doc('Privacy Notice',
    `This notice explains what ${C} does with personal data when you pay through a WyoCSPay page.`,
    [
      ['What we hold', [
        'The amount, currency, date and status of your payment; the merchant it was made to; and the reference the merchant gave it.',
        'The card brand and last four digits, and the payment method type. Nothing more of the card.',
        'Your email address, if the merchant supplied one so that a receipt can be sent.',
        'Technical data needed to run the page safely: IP address, browser type, and the time of each request.',
      ]],
      ['What we never hold', [
        'The full card number, the security code, the expiry date, the PIN, or any magnetic stripe data. These go from your browser to Stripe and never reach us. There is no field for them anywhere in our systems.',
      ]],
      ['Why we hold it', [
        'To take the payment you asked us to take, and to show the merchant that you paid.',
        'To detect and investigate fraud, and to check that our record of a payment matches the payment network’s record.',
        'To meet our legal obligations, including financial record keeping and anti money laundering rules.',
      ]],
      ['Who else sees it', [
        'Stripe, Inc. and its affiliates, as our payment processor. Stripe has its own privacy policy at stripe.com/privacy.',
        'The merchant you paid, so that they can fulfil your order and handle refunds.',
        'Authorities, where the law requires it. We do not sell personal data and we do not use it for advertising.',
      ]],
      ['How long we keep it', [
        'Payment records are kept for seven years, the period required for financial records and for disputes that can be raised long after a payment. Technical logs are kept for a shorter period.',
      ]],
      ['Your rights', [
        'You may ask what we hold about you, ask for it to be corrected, ask for it to be deleted where we are not required to keep it, and object to certain processing.',
        'In the European Economic Area or the United Kingdom you may complain to your data protection authority. In California you have rights under the CCPA, including the right to know and the right to delete.',
        `To exercise any of these, write to ${E}. We reply within 30 days.`,
      ]],
      ['Where your data is held', [
        'We are established in the United States and our systems and backups are hosted there. Payment data is processed by Stripe, which operates globally.',
        'Where data about someone in the European Economic Area or the United Kingdom is transferred to the United States, that transfer is covered by the Standard Contractual Clauses or another lawful transfer mechanism.',
        'Where a bank account is linked to authorise a payment or a payout, the account and routing number, the balance and the name and address of the account holder are read for that purpose alone. We do not retrieve transaction history. This data is stored only in the United States.',
      ]],
      ['Contact', identity('Managing member:', 'Email:')],
    ]),

  bg: doc('Политика за поверителност',
    `Този документ обяснява какво ${C} прави с личните данни, когато плащате през страница на WyoCSPay.`,
    [
      ['Какво съхраняваме', [
        'Сумата, валутата, датата и статуса на плащането; търговеца, към когото е направено; и референцията, която той му е дал.',
        'Марката на картата и последните четири цифри, както и вида на платежния метод. Нищо повече от картата.',
        'Имейл адреса Ви, ако търговецът го е предоставил, за да бъде изпратена разписка.',
        'Технически данни, нужни за безопасната работа на страницата: IP адрес, вид на браузъра и време на всяка заявка.',
      ]],
      ['Какво никога не съхраняваме', [
        'Пълния номер на картата, кода за сигурност, срока на валидност, ПИН или данни от магнитната лента. Те отиват от браузъра Ви директно в Stripe и не достигат до нас. В нашите системи не съществува поле за тях.',
      ]],
      ['Защо ги съхраняваме', [
        'За да извършим плащането, което сте поискали, и за да покажем на търговеца, че сте платили.',
        'За откриване и разследване на измами и за проверка, че нашият запис съответства на записа на платежната мрежа.',
        'За изпълнение на законови задължения, включително счетоводни изисквания и правила срещу изпиране на пари.',
      ]],
      ['Кой друг ги вижда', [
        'Stripe, Inc. и свързаните с него дружества, като наш платежен процесор. Stripe има собствена политика на stripe.com/privacy.',
        'Търговецът, на когото сте платили, за да изпълни поръчката Ви и да обработва възстановявания.',
        'Държавни органи, когато законът го изисква. Не продаваме лични данни и не ги използваме за реклама.',
      ]],
      ['Колко дълго', [
        'Платежните записи се пазят седем години, срокът, изискван за счетоводни документи и за спорове, които могат да бъдат повдигнати дълго след плащането. Техническите дневници се пазят по-кратко.',
      ]],
      ['Вашите права', [
        'Може да поискате да узнаете какво съхраняваме за Вас, да поискате коригиране, изтриване там, където не сме задължени да пазим данните, и да възразите срещу определени обработки.',
        'В Европейското икономическо пространство или Обединеното кралство може да подадете жалба до надзорния орган. В България това е Комисията за защита на личните данни.',
        `За упражняване на тези права пишете на ${E}. Отговаряме в срок от 30 дни.`,
      ]],
      ['Международни трансфери', [
        'Установени сме в САЩ, а Stripe работи в глобален мащаб. Когато данни напускат ЕИП или Обединеното кралство, това става въз основа на Стандартни договорни клаузи или друг законен механизъм.',
      ]],
      ['Контакт', identity('Управляващ съдружник:', 'Имейл:')],
    ]),

  de: doc('Datenschutzhinweis',
    `Dieser Hinweis erklärt, was ${C} mit personenbezogenen Daten tut, wenn Sie über eine WyoCSPay-Seite bezahlen.`,
    [
      ['Was wir speichern', [
        'Betrag, Währung, Datum und Status Ihrer Zahlung; den Händler, an den sie ging; und dessen Referenz.',
        'Kartenmarke und die letzten vier Ziffern sowie die Zahlungsart. Nichts weiter von der Karte.',
        'Ihre E-Mail-Adresse, sofern der Händler sie für den Beleg übermittelt hat.',
        'Technische Daten für den sicheren Betrieb: IP-Adresse, Browsertyp und Zeitpunkt jeder Anfrage.',
      ]],
      ['Was wir nie speichern', [
        'Die vollständige Kartennummer, die Prüfziffer, das Ablaufdatum, die PIN oder Magnetstreifendaten. Diese gehen von Ihrem Browser direkt an Stripe. In unseren Systemen existiert kein Feld dafür.',
      ]],
      ['Warum wir sie speichern', [
        'Um die Zahlung auszuführen und dem Händler zu belegen, dass Sie bezahlt haben.',
        'Zur Erkennung und Untersuchung von Betrug und zum Abgleich unserer Aufzeichnungen mit denen des Zahlungsnetzwerks.',
        'Zur Erfüllung gesetzlicher Pflichten, einschließlich Aufbewahrungs- und Geldwäschevorschriften.',
      ]],
      ['Wer sie sonst sieht', [
        'Stripe, Inc. und verbundene Unternehmen als unser Zahlungsdienstleister. Stripe hat eine eigene Datenschutzerklärung unter stripe.com/privacy.',
        'Der Händler, an den Sie gezahlt haben, zur Auftragsabwicklung und für Erstattungen.',
        'Behörden, soweit gesetzlich vorgeschrieben. Wir verkaufen keine personenbezogenen Daten und nutzen sie nicht für Werbung.',
      ]],
      ['Wie lange', [
        'Zahlungsdaten werden sieben Jahre aufbewahrt, der für Finanzunterlagen und für später erhobene Streitfälle erforderliche Zeitraum. Technische Protokolle werden kürzer aufbewahrt.',
      ]],
      ['Ihre Rechte', [
        'Sie können Auskunft, Berichtigung und Löschung verlangen, soweit wir nicht zur Aufbewahrung verpflichtet sind, und bestimmten Verarbeitungen widersprechen.',
        'Im EWR und im Vereinigten Königreich können Sie sich bei Ihrer Datenschutzbehörde beschweren.',
        `Zur Ausübung schreiben Sie an ${E}. Wir antworten binnen 30 Tagen.`,
      ]],
      ['Internationale Übermittlungen', [
        'Wir sitzen in den USA, Stripe arbeitet weltweit. Verlassen Daten den EWR oder das Vereinigte Königreich, geschieht dies auf Grundlage der Standardvertragsklauseln oder eines anderen zulässigen Mechanismus.',
      ]],
      ['Kontakt', identity('Geschäftsführendes Mitglied:', 'E-Mail:')],
    ]),

  fr: doc('Politique de confidentialité',
    `Cette politique explique ce que ${C} fait des données personnelles lorsque vous payez via une page WyoCSPay.`,
    [
      ['Ce que nous conservons', [
        'Le montant, la devise, la date et le statut de votre paiement ; le marchand destinataire ; et la référence qu’il lui a donnée.',
        'La marque de la carte et les quatre derniers chiffres, ainsi que le type de moyen de paiement. Rien de plus de la carte.',
        'Votre adresse e-mail, si le marchand l’a fournie pour l’envoi d’un reçu.',
        'Les données techniques nécessaires au fonctionnement sûr de la page : adresse IP, type de navigateur et horodatage de chaque requête.',
      ]],
      ['Ce que nous ne conservons jamais', [
        'Le numéro complet de la carte, le cryptogramme, la date d’expiration, le code PIN ou les données de piste magnétique. Ces éléments vont de votre navigateur à Stripe. Aucun champ n’existe pour eux dans nos systèmes.',
      ]],
      ['Pourquoi nous les conservons', [
        'Pour exécuter le paiement demandé et prouver au marchand que vous avez payé.',
        'Pour détecter et enquêter sur la fraude, et vérifier que notre enregistrement correspond à celui du réseau de paiement.',
        'Pour respecter nos obligations légales, y compris comptables et de lutte contre le blanchiment.',
      ]],
      ['Qui d’autre y accède', [
        'Stripe, Inc. et ses filiales, en tant que prestataire de paiement. Stripe dispose de sa propre politique sur stripe.com/privacy.',
        'Le marchand que vous avez payé, pour honorer votre commande et traiter les remboursements.',
        'Les autorités, lorsque la loi l’exige. Nous ne vendons pas de données personnelles et ne les utilisons pas à des fins publicitaires.',
      ]],
      ['Durée de conservation', [
        'Les enregistrements de paiement sont conservés sept ans, durée requise pour les documents financiers et les litiges pouvant survenir bien après. Les journaux techniques sont conservés moins longtemps.',
      ]],
      ['Vos droits', [
        'Vous pouvez demander l’accès, la rectification, l’effacement lorsque nous ne sommes pas tenus de conserver, et vous opposer à certains traitements.',
        'Dans l’EEE et au Royaume-Uni, vous pouvez saisir votre autorité de protection des données. En France, la CNIL.',
        `Pour exercer ces droits, écrivez à ${E}. Nous répondons sous 30 jours.`,
      ]],
      ['Transferts internationaux', [
        'Nous sommes établis aux États-Unis et Stripe opère mondialement. Lorsque des données quittent l’EEE ou le Royaume-Uni, elles sont couvertes par les clauses contractuelles types ou un autre mécanisme licite.',
      ]],
      ['Contact', identity('Membre gérant :', 'E-mail :')],
    ]),

  es: doc('Aviso de privacidad',
    `Este aviso explica qué hace ${C} con los datos personales cuando usted paga a través de una página WyoCSPay.`,
    [
      ['Qué conservamos', [
        'El importe, la moneda, la fecha y el estado de su pago; el comercio destinatario; y la referencia que este le dio.',
        'La marca de la tarjeta y los últimos cuatro dígitos, y el tipo de método de pago. Nada más de la tarjeta.',
        'Su dirección de correo, si el comercio la facilitó para enviar un recibo.',
        'Datos técnicos necesarios para el funcionamiento seguro: dirección IP, tipo de navegador y hora de cada solicitud.',
      ]],
      ['Qué nunca conservamos', [
        'El número completo de la tarjeta, el código de seguridad, la fecha de caducidad, el PIN o datos de banda magnética. Van de su navegador a Stripe. No existe ningún campo para ellos en nuestros sistemas.',
      ]],
      ['Por qué los conservamos', [
        'Para ejecutar el pago solicitado y demostrar al comercio que usted pagó.',
        'Para detectar e investigar fraude, y comprobar que nuestro registro coincide con el de la red de pagos.',
        'Para cumplir obligaciones legales, incluidas las contables y de prevención de blanqueo.',
      ]],
      ['Quién más los ve', [
        'Stripe, Inc. y sus filiales, como procesador de pagos. Stripe tiene su propia política en stripe.com/privacy.',
        'El comercio al que pagó, para cumplir su pedido y gestionar reembolsos.',
        'Las autoridades, cuando la ley lo exige. No vendemos datos personales ni los usamos para publicidad.',
      ]],
      ['Cuánto tiempo', [
        'Los registros de pago se conservan siete años, el periodo exigido para documentos financieros y para disputas que pueden plantearse mucho después. Los registros técnicos se conservan menos tiempo.',
      ]],
      ['Sus derechos', [
        'Puede solicitar acceso, rectificación, supresión cuando no estemos obligados a conservar, y oponerse a ciertos tratamientos.',
        'En el EEE y el Reino Unido puede reclamar ante su autoridad de protección de datos. En España, la AEPD.',
        `Para ejercerlos, escriba a ${E}. Respondemos en 30 días.`,
      ]],
      ['Transferencias internacionales', [
        'Estamos establecidos en Estados Unidos y Stripe opera globalmente. Cuando los datos salen del EEE o del Reino Unido, quedan cubiertos por las Cláusulas Contractuales Tipo u otro mecanismo lícito.',
      ]],
      ['Contacto', identity('Miembro gestor:', 'Correo:')],
    ]),

  it: doc('Informativa sulla privacy',
    `Questa informativa spiega cosa fa ${C} con i dati personali quando paghi tramite una pagina WyoCSPay.`,
    [
      ['Cosa conserviamo', [
        'Importo, valuta, data e stato del pagamento; l’esercente destinatario; e il riferimento da lui assegnato.',
        'Il circuito della carta e le ultime quattro cifre, oltre al tipo di metodo di pagamento. Nulla di più della carta.',
        'Il tuo indirizzo email, se l’esercente lo ha fornito per l’invio di una ricevuta.',
        'Dati tecnici necessari al funzionamento sicuro: indirizzo IP, tipo di browser e orario di ogni richiesta.',
      ]],
      ['Cosa non conserviamo mai', [
        'Il numero completo della carta, il codice di sicurezza, la scadenza, il PIN o i dati della banda magnetica. Vanno dal tuo browser a Stripe. Nei nostri sistemi non esiste alcun campo per essi.',
      ]],
      ['Perché li conserviamo', [
        'Per eseguire il pagamento richiesto e dimostrare all’esercente che hai pagato.',
        'Per rilevare e indagare frodi e verificare che il nostro registro coincida con quello del circuito.',
        'Per adempiere a obblighi di legge, inclusi quelli contabili e antiriciclaggio.',
      ]],
      ['Chi altro li vede', [
        'Stripe, Inc. e le sue affiliate, come processore di pagamento. Stripe ha una propria informativa su stripe.com/privacy.',
        'L’esercente a cui hai pagato, per evadere l’ordine e gestire i rimborsi.',
        'Le autorità, quando la legge lo richiede. Non vendiamo dati personali e non li usiamo per pubblicità.',
      ]],
      ['Per quanto tempo', [
        'I registri di pagamento sono conservati sette anni, il periodo richiesto per i documenti finanziari e per contestazioni sollevabili molto dopo. I log tecnici sono conservati meno a lungo.',
      ]],
      ['I tuoi diritti', [
        'Puoi chiedere accesso, rettifica, cancellazione ove non siamo tenuti a conservare, e opporti a determinati trattamenti.',
        'Nel SEE e nel Regno Unito puoi rivolgerti alla tua autorità di protezione dei dati. In Italia, il Garante Privacy.',
        `Per esercitarli scrivi a ${E}. Rispondiamo entro 30 giorni.`,
      ]],
      ['Trasferimenti internazionali', [
        'Siamo stabiliti negli Stati Uniti e Stripe opera a livello globale. Quando i dati lasciano il SEE o il Regno Unito sono coperti dalle Clausole Contrattuali Standard o da altro meccanismo lecito.',
      ]],
      ['Contatti', identity('Membro gestore:', 'Email:')],
    ]),

  ru: doc('Политика конфиденциальности',
    `Этот документ объясняет, что ${C} делает с персональными данными, когда вы платите через страницу WyoCSPay.`,
    [
      ['Что мы храним', [
        'Сумму, валюту, дату и статус платежа; продавца, которому он сделан; и присвоенную им ссылку.',
        'Платёжную систему карты и последние четыре цифры, а также тип способа оплаты. Ничего больше о карте.',
        'Ваш адрес электронной почты, если продавец предоставил его для отправки чека.',
        'Технические данные для безопасной работы страницы: IP-адрес, тип браузера и время каждого запроса.',
      ]],
      ['Что мы никогда не храним', [
        'Полный номер карты, код безопасности, срок действия, ПИН или данные магнитной полосы. Они идут из вашего браузера напрямую в Stripe. В наших системах для них нет ни одного поля.',
      ]],
      ['Зачем мы их храним', [
        'Чтобы выполнить запрошенный платёж и подтвердить продавцу, что вы заплатили.',
        'Для выявления и расследования мошенничества и сверки нашей записи с записью платёжной сети.',
        'Для исполнения обязанностей по закону, включая бухгалтерский учёт и противодействие отмыванию денег.',
      ]],
      ['Кто ещё их видит', [
        'Stripe, Inc. и связанные компании как наш платёжный процессор. У Stripe своя политика на stripe.com/privacy.',
        'Продавец, которому вы заплатили, чтобы выполнить заказ и обработать возвраты.',
        'Государственные органы, когда этого требует закон. Мы не продаём персональные данные и не используем их для рекламы.',
      ]],
      ['Сколько мы их храним', [
        'Записи о платежах хранятся семь лет, срок, требуемый для финансовых документов и для споров, которые могут возникнуть значительно позже. Технические журналы хранятся меньше.',
      ]],
      ['Ваши права', [
        'Вы можете запросить сведения о том, что мы храним, потребовать исправления, удаления там, где мы не обязаны хранить, и возразить против отдельных видов обработки.',
        'В ЕЭП и Великобритании вы можете подать жалобу в надзорный орган по защите данных.',
        `Для реализации этих прав пишите на ${E}. Мы отвечаем в течение 30 дней.`,
      ]],
      ['Международная передача', [
        'Мы находимся в США, а Stripe работает по всему миру. Когда данные покидают ЕЭП или Великобританию, это происходит на основании Стандартных договорных условий или иного законного механизма.',
      ]],
      ['Контакты', identity('Управляющий участник:', 'Эл. почта:')],
    ]),

  uk: doc('Політика конфіденційності',
    `Цей документ пояснює, що ${C} робить з персональними даними, коли ви платите через сторінку WyoCSPay.`,
    [
      ['Що ми зберігаємо', [
        'Суму, валюту, дату та статус платежу; продавця, якому він зроблений; і присвоєний ним референс.',
        'Платіжну систему картки та останні чотири цифри, а також тип способу оплати. Нічого більше про картку.',
        'Вашу адресу електронної пошти, якщо продавець надав її для надсилання чека.',
        'Технічні дані для безпечної роботи сторінки: IP-адресу, тип браузера та час кожного запиту.',
      ]],
      ['Чого ми ніколи не зберігаємо', [
        'Повний номер картки, код безпеки, термін дії, ПІН або дані магнітної смуги. Вони йдуть з вашого браузера безпосередньо у Stripe. У наших системах для них немає жодного поля.',
      ]],
      ['Навіщо ми їх зберігаємо', [
        'Щоб виконати запитаний платіж і підтвердити продавцю, що ви заплатили.',
        'Для виявлення та розслідування шахрайства й звірки нашого запису із записом платіжної мережі.',
        'Для виконання обовʼязків за законом, включно з бухгалтерським обліком та протидією відмиванню коштів.',
      ]],
      ['Хто ще їх бачить', [
        'Stripe, Inc. та повʼязані компанії як наш платіжний процесор. У Stripe власна політика на stripe.com/privacy.',
        'Продавець, якому ви заплатили, щоб виконати замовлення та обробити повернення.',
        'Державні органи, коли цього вимагає закон. Ми не продаємо персональні дані й не використовуємо їх для реклами.',
      ]],
      ['Скільки ми їх зберігаємо', [
        'Записи про платежі зберігаються сім років, строк, потрібний для фінансових документів і для спорів, які можуть виникнути значно пізніше. Технічні журнали зберігаються менше.',
      ]],
      ['Ваші права', [
        'Ви можете запитати відомості про те, що ми зберігаємо, вимагати виправлення, видалення там, де ми не зобовʼязані зберігати, і заперечити проти окремих видів обробки.',
        'У ЄЕП та Великій Британії ви можете подати скаргу до наглядового органу із захисту даних.',
        `Для реалізації цих прав пишіть на ${E}. Ми відповідаємо протягом 30 днів.`,
      ]],
      ['Міжнародна передача', [
        'Ми розташовані у США, а Stripe працює по всьому світу. Коли дані залишають ЄЕП або Велику Британію, це відбувається на підставі Стандартних договірних умов або іншого законного механізму.',
      ]],
      ['Контакти', identity('Керуючий учасник:', 'Ел. пошта:')],
    ]),
};

// ---------------------------------------------------------------------------

/**
 * Every language has both documents, so there is no English fallback and no
 * notice telling a customer their language is second class.
 */
export function legalDocument(lang: string, which: 'terms' | 'privacy'): LegalDocument {
  const code = (lang in terms ? lang : 'en') as Lang;
  return which === 'terms' ? terms[code] : privacy[code];
}
